import {
  AbortError,
  CustomerDesignResponse,
  getCreateWork,
  getPriceForProductAndOptions,
  getUploadFileRequirements,
  PricingResult,
  processUploadClient,
  ProductOptions,
  UnembeddedFontsServerError,
  validateDesign,
  Validation,
  ValidationState,
  ValidatorType,
} from '@vp/upload-and-print-validator';
import { AnalyticsEventData, sendEventTracking } from 'src/components/Analytics';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { TrackingConfiguration } from 'src/providers/TrackingContextProvider';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { currentCulture } from 'src/utils/cultureUtil';
import { delay } from 'src/utils/delay';
import { addPageAction, noticeError } from 'src/utils/newRelicUtil';
import { parseAPIErrorResponse } from 'src/utils/parseAPIErrorResponse';
import { TrackingDataForProduct } from 'src/utils/trackingUtil';

export interface Upload {
  fileUri: string;
  uploadedFileName?: string;
  processedUploadUri?: string;
  thumbnailUrl?: string;
  renderingInstructionsUrl?: string;
  cimDocUrl?: string;
  fileSize?: number;
}

export interface OptionsAndPrice {
  productOptions: { [name: string]: string };
  price: PricingResult;
}

export interface CreateWorkResponse {
  workId: string;
  nextPageUrl: string;
} //TODO: this should be exported from validator package

export async function pollResultUrl(resultUrl: string, abortSignal?: AbortSignal): Promise<string> {
  try {
    if (abortSignal?.aborted) {
      throw new AbortError('Aborted pooling result URL');
    }

    const start = Date.now();
    const timeout = 300000;
    const delayTime = 1000;
    while (Date.now() - start < timeout) {
      if (abortSignal?.aborted) {
        throw new AbortError('Aborted looping result URL');
      }
      const response = await fetch(resultUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortSignal,
      });
      if (!response.ok) {
        throw new Error(`Invalid response processing upload: ${await parseAPIErrorResponse(response)}`);
      }

      const result = await response.json();
      if (result.Output) {
        return result.Output;
      } else if (result.Status === 'Error') {
        throw new Error(JSON.stringify(result.Exception));
      }
      await delay(delayTime);
    }
    throw new Error(`Timed out polling result url`);
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      throw error;
    }
    noticeError(error, {
      resultUrl: resultUrl,
    });
    throw new Error(`Error polling result URL: ${(error as Error).message}`);
  }
}

export async function processAndValidateUpload(
  fileUri: string,
  productInformation: ProductInformation,
  trackingConfiguration: TrackingConfiguration | null,
  trackingDataForProduct: TrackingDataForProduct,
  isVat: boolean,
  fixedPageSizeWarning: (status: boolean) => void,
  abortSignal?: AbortSignal
): Promise<[string, CustomerDesignResponse]> {
  try {
    const startTime = new Date().getTime();
    const uploadResult = await processUploadClient(
      {
        fileUri,
        productKey: productInformation.productKey,
        productVersion: productInformation.productVersion,
        productOptions: productInformation.selectedOptions,
        culture: currentCulture.toLowerCase(),
      },
      { ...getAuthHeader(), signal: abortSignal }
    );

    let processedUploadUri = await pollResultUrl(uploadResult.resultUrl, abortSignal);
    const processingEndTime = new Date().getTime();

    const validatedUpload = await validateDesign(
      {
        fileUri: processedUploadUri,
        productKey: productInformation.productKey,
        productVersion: productInformation.productVersion,
        quantity: productInformation.quantity,
        culture: currentCulture.toLocaleLowerCase(),
        isVat,
        suggestNewProductOptions: true,
        productOptions: productInformation.selectedOptions,
        pricingContextId: undefined,
      },
      { ...getAuthHeader(), signal: abortSignal }
    );

    const validationEndTime = new Date().getTime();

    const processingDuration = processingEndTime - startTime;
    const validationDuration = validationEndTime - processingEndTime;
    addPageAction('All Server Side validation', {
      fileUri,
      durationMs: validationDuration,
      serverValidationOutput: JSON.stringify(validatedUpload.validations),
    });
    validatedUpload.validations.forEach(validation => {
      addPageAction(`File ${validation.id} serverSide Check`, {
        fileUri,
        state: validation.state,
      });
    });

    sendEventTracking({
      ...trackingConfiguration,
      ...trackingDataForProduct,
      action: UPLOAD_UNIFIED_EVENT,
      label: 'Document Processing Completed',
      uploadStep: UploadSteps.ProcessingCompleted,
      category: 'Design - Upload and Print',
      eventDetail: `processingDuration:${processingDuration};validationDuration:${validationDuration}`,
    } as AnalyticsEventData);

    // The upload Uri could change if processUploadResult fixes the document
    processedUploadUri = validatedUpload.documentUploadOutputUrl;

    /**
     * Check for page size auto fix only if there are no errors in validations
     */
    if (!validatedUpload.validations.some(validation => validation.state === ValidationState.Error)) {
      const updatedValidations = validatedUpload.validations.map(validation => {
        if (validation.id === ValidatorType.PageSizeValidator && validation.state === ValidationState.Warning) {
          fixedPageSizeWarning(true);
          return {
            ...validation,
            state: ValidationState.Ok,
            message: '',
          };
        }
        return validation;
      });
      validatedUpload.validations = updatedValidations;
    }

    return [processedUploadUri, validatedUpload];
  } catch (e) {
    if ((e as Error).name === 'AbortError') {
      throw e;
    }
    if (e instanceof UnembeddedFontsServerError) {
      throw e;
    }
    throw new Error(`Error in validating design, ${e}`);
  }
}

export async function retrieveFileRequirements(
  productKey: string,
  productVersion: number,
  productOptions: ProductOptions
): Promise<Validation[] | []> {
  const header = getAuthHeader();

  if (header.headers.Authorization === 'Bearer null') {
    return [];
  }
  return await getUploadFileRequirements(
    { productKey, productVersion, productOptions, culture: currentCulture.toLowerCase() },
    header
  );
}

export async function getPrice(
  productKey: string,
  productVersion: number,
  culture: string,
  quantity: number,
  productOptions: { [name: string]: string },
  isVat: boolean
): Promise<PricingResult | null> {
  const header = getAuthHeader();

  if (header.headers.Authorization === 'Bearer null') {
    return null;
  }
  return await getPriceForProductAndOptions(
    {
      productKey,
      productVersion,
      culture,
      quantity,
      isVat,
      productOptions,
    },
    header
  );
}

export async function createWork(
  fileUri: string,
  workName: string,
  productKey: string,
  productVersion: number,
  quantity: number,
  culture: string,
  workId: string | null,
  productOptions: { [name: string]: string },
  editUrl: string | null,
  createdBy: string | null,
  vistaCreateProjectId: string | null
): Promise<CreateWorkResponse> {
  return await getCreateWork(
    {
      fileuri: fileUri,
      workName,
      productKey,
      productVersion,
      productOptions,
      quantity,
      culture,
      workId,
      editUrl,
      createdBy,
      vistaCreateProjectId,
    },
    getAuthHeader()
  );
}
